import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { put } from "../../lib/api";
import { type PriceForm, priceFormOf, readPriceForm } from "../../lib/priceInput";
import type { PriceLineage } from "../../lib/priceLineage";
import { perKwh } from "../../lib/tariff";
import type { PriceChange } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice, Notice } from "../ui/Feedback";
import { MicroLabel } from "../ui/Text";
import { PriceFields } from "./PriceFields";

/**
 * Changing a price. pay-ui spec §6.1.
 *
 * ## What pressing save actually does
 *
 * Not an edit. The server mints a **new version** of this price, retires the one on
 * screen, and moves every charge point subscribed to the price onto the new version —
 * one transaction. Orders already placed keep pointing at the version they were quoted
 * at, which is what makes a receipt still true a year later.
 *
 * That is also why this form warns before it is submitted rather than confirming
 * after: it is the only screen in the panel where one button repriced stations the
 * operator did not name, and the count of them is knowable in advance. The response
 * says how many were actually moved, and the page reports that figure rather than the
 * one predicted here — between opening the form and saving it, a station can have been
 * put on the price by somebody else.
 *
 * ## Two things it will not offer
 *
 * The **currency** is locked: the server refuses to change it, because every station
 * on the price would start asking drivers for a different money and the versions could
 * no longer be summed together. That is a new price.
 *
 * There is no **retire**. Stopping a price without a successor has to answer where its
 * stations go, and nothing in the API answers that yet — a button here would be an
 * answer shipped quietly.
 *
 * ## A rename is not a change
 *
 * Correcting the label mints nothing and moves nobody; the server applies it in place.
 * So this same form covers both, and which one happened is the server's word
 * (`rotated`) rather than something read off the fields.
 */

export interface ChangePriceProps {
  readonly price: PriceLineage;
  readonly onChanged: (change: PriceChange) => void;
  readonly onCancel: () => void;
}

export function ChangePrice({ price, onChanged, onCancel }: ChangePriceProps) {
  const queryClient = useQueryClient();
  const current = price.current;
  // Seeded from the version in force, in the major units it was typed in. Reading it
  // back unchanged is a no-op the server recognises as one — see `priceFormOf`.
  const [form, setForm] = useState<PriceForm>(() => priceFormOf(current));

  const draft = readPriceForm(form);
  const issues = draft.ok ? {} : draft.issues;

  const change = useMutation({
    mutationFn: (body: unknown) => put<PriceChange>(`/tariffs/${current.id}`, body),
    onSuccess: (result) => {
      /**
       * Both lists, and the fleet is not optional: a rotation changed
       * `charge_point.tariff_id` on every subscribed station, so a fleet table left
       * on its cached rows would name a version that is no longer in force.
       */
      void queryClient.invalidateQueries({ queryKey: ["tariffs"] });
      void queryClient.invalidateQueries({ queryKey: ["charge-points"] });
      // And any station detail left open in another tab of this session: a rotation
      // moves stations nobody named, so there is no one station to invalidate.
      void queryClient.invalidateQueries({ queryKey: ["charge-point"] });
      onChanged(result);
    },
  });

  const priceMoved = draft.ok && draft.body.priceMinorPerKwh !== current.priceMinorPerKwh;
  const canSubmit = draft.ok && !change.isPending;

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>Change {current.name}</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {change.isError ? <ErrorNotice error={change.error} /> : null}

      {/*
        The blast radius, before the button rather than after it. Suppressed at zero
        subscribers: a price nothing is selling on is an ordinary edit, and a warning
        that fires every time is one nobody reads on the occasion that matters.
      */}
      {price.subscriberCount > 0 ? (
        <Notice tone="warn">
          {price.subscriberCount === 1
            ? "1 station is on this price and will be moved to the new version."
            : `${price.subscriberCount} stations are on this price and will be moved to the new version.`}
          {priceMoved
            ? ` Drivers there will be quoted ${perKwh(draft.ok ? draft.body.priceMinorPerKwh : current.priceMinorPerKwh, current.currency)} instead of ${perKwh(current.priceMinorPerKwh, current.currency)} from the moment this is saved.`
            : ""}
        </Notice>
      ) : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!draft.ok || change.isPending) return;
          change.mutate(draft.body);
        }}
        className="flex flex-col gap-5"
      >
        <PriceFields
          form={form}
          issues={issues}
          onChange={(field, value) => setForm((state) => ({ ...state, [field]: value }))}
          currencyLocked
        />

        <div className="flex items-end">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={change.isPending}>
            Save new version
          </Button>
        </div>
      </form>

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        Saving mints a new version of this price and retires the current one. Orders already placed
        keep the version they were quoted at, so nothing already charged for changes. Correcting
        only the name mints nothing.
      </p>
    </div>
  );
}
